import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import type { LeaderRow } from '../store/gameStore';

/**
 * Leaderboard — desktop shows full 8-row panel top-right; on MOBILE
 * the entire component returns null at render time (no DOM at all).
 *
 * The mobile early-return is deliberate redundancy on top of the
 * `hidden` Tailwind class on the mobile chip: the operator reported
 * THREE times that a "players' table at bottom-right" was still
 * visible, which means either the service worker was serving stale
 * code OR some browser was bypassing the CSS rule. Returning null
 * unconditionally on mobile is the surest fix.
 */

/** Same detection rule used elsewhere in HUD — small viewport OR
 *  any touch capability. Re-evaluated on resize / orientation. */
function useIsMobileLike(): boolean {
  const [mobile, setMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 1024px)').matches ||
           'ontouchstart' in window ||
           (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 0);
  });
  useEffect(() => {
    const onResize = (): void => {
      const small = window.matchMedia('(max-width: 1024px)').matches;
      const touch = 'ontouchstart' in window ||
                    (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 0);
      setMobile(small || touch);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return mobile;
}

export function Leaderboard({
  rows,
}: {
  rows: LeaderRow[];
}) {
  const isMobile = useIsMobileLike();
  // ⛔ On mobile/touch devices the leaderboard NEVER renders. The
  // operator wanted zero UI on the right edge so the playable arena
  // owns the full screen. Player rank is still visible in the
  // IdentityCard (top-left) and in the death modal recap.
  if (isMobile) return null;
  const maxMass = rows[0]?.mass ?? 1;

  // Compact mode: show just the player's rank as a small chip in the
  // top-right corner, expand to full list on tap. Default state =
  // collapsed on mobile, always-expanded on desktop (media query
  // governs which renderer mounts at all, but we also expose the
  // collapsed/expanded state for the rare wide-mobile case).
  const [expanded, setExpanded] = useState(false);
  // Auto-collapse after 4 s of no further taps so the expanded panel
  // doesn't permanently cover the screen during play.
  useEffect(() => {
    if (!expanded) return;
    const t = window.setTimeout(() => setExpanded(false), 4000);
    return () => clearTimeout(t);
  }, [expanded]);

  // Find the local player's row for the compact-chip readout. Falls
  // back to the rank=playerRank metadata if the player is below the
  // top-8 cut.
  const me = rows.find((r) => r.isPlayer);
  const myRank = me?.playerRank ?? ((rows.findIndex((r) => r.isPlayer) + 1) || rows.length);
  const myColor = me ? `hsl(${me.hue} 100% 65%)` : 'hsl(190 100% 65%)';

  return (
    <>
      {/* ── Mobile: hidden entirely. Per operator request the
                       right edge of the screen is reserved for the ▶
                       turn pad. Players can check their rank via the
                       leaderboard expansion in Settings (or by
                       glancing at the IdentityCard which still shows
                       their mass). */}
      <div className="hidden absolute top-3 right-3 z-30 pointer-events-auto">
        <button
          onClick={() => setExpanded((v) => !v)}
          aria-label="Show leaderboard"
          className="hud-panel inline-flex items-center gap-2 px-3 py-1.5 active:scale-95 transition-transform"
          style={{
            background: 'rgba(10,16,28,0.72)',
            border: `1px solid ${myColor}55`,
            boxShadow: `0 0 14px ${myColor}33`,
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: myColor, boxShadow: `0 0 6px ${myColor}` }}
          />
          <span className="text-[11px] uppercase tracking-[0.18em] text-white/70 font-bold">
            <span style={{ color: myColor }}>#{myRank || '—'}</span>
            <span className="text-white/35 mx-1">/</span>
            <span>{rows.length}</span>
          </span>
        </button>

        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.97, transition: { duration: 0.14 } }}
              transition={{ type: 'spring', stiffness: 400, damping: 28 }}
              className="hud-panel absolute top-full right-0 mt-2 px-3 py-2 w-[min(260px,calc(100vw-24px))]"
              onClick={() => setExpanded(false)}
            >
              <div className="text-[9px] uppercase tracking-[0.25em] text-white/45 mb-1.5">
                Leaderboard
              </div>
              <LeaderboardRows rows={rows} maxMass={maxMass} compact />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Desktop: persistent panel top-right with overtake hint ── */}
      <div className="hidden md:block hud-panel absolute top-3 right-3 px-3 py-2 w-[clamp(200px,17vw,300px)]">
        <div className="text-[clamp(9px,0.55vw,11px)] uppercase tracking-[0.25em] text-white/45 mb-1.5">
          Leaderboard
        </div>
        <LeaderboardRows rows={rows} maxMass={maxMass} compact={false} />
        <OvertakeHint rows={rows} />
      </div>
    </>
  );
}

/** The actual rank list — shared between desktop persistent panel and
 *  mobile expanded sheet. `compact` flag tightens font + spacing for
 *  the mobile pop-out where vertical space is at a premium. */
function LeaderboardRows({
  rows,
  maxMass,
  compact,
}: {
  rows: LeaderRow[];
  maxMass: number;
  compact: boolean;
}) {
  const fontRank = compact ? 'text-[11px]' : 'text-[clamp(10px,0.6vw,12px)]';
  const fontName = compact ? 'text-[12px]' : 'text-[clamp(11px,0.7vw,14px)]';
  const fontMass = compact ? 'text-[11px]' : 'text-[clamp(10px,0.6vw,12px)]';

  return (
    <motion.ol className="space-y-0.5" layout>
      <AnimatePresence initial={false}>
        {rows.map((row, i) => {
          const rank = row.playerRank ?? i + 1;
          const widthPct = Math.max(4, Math.min(100, (row.mass / maxMass) * 100));
          const dotColor = `hsl(${row.hue} 100% 60%)`;
          return (
            <motion.li
              key={row.id}
              layout
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 16, transition: { duration: 0.15 } }}
              transition={{ type: 'spring', stiffness: 360, damping: 32 }}
              className={`relative px-1.5 py-0.5 rounded ${
                row.isPlayer ? 'ring-1 ring-white/15' : ''
              }`}
              style={
                row.isPlayer
                  ? { background: `linear-gradient(90deg, ${dotColor}22, transparent)` }
                  : undefined
              }
            >
              <div
                className="absolute inset-y-0 left-0 rounded opacity-25 pointer-events-none transition-[width] duration-200 ease-out"
                style={{
                  width: `${widthPct}%`,
                  background: `linear-gradient(90deg, hsl(${row.hue} 100% 55%) 0%, transparent 100%)`,
                }}
              />
              <div className="relative flex items-center gap-1.5">
                <span
                  className={`tabular-nums w-3 text-right font-semibold ${fontRank} ${
                    row.isPlayer ? 'text-white' : ''
                  }`}
                  style={
                    row.isPlayer
                      ? undefined
                      : rank === 1
                        ? { color: '#ffd23d', textShadow: '0 0 6px rgba(255,210,61,0.6)' }
                        : rank === 2
                          ? { color: '#cfd8dc' }
                          : rank === 3
                            ? { color: '#d8956b' }
                            : { color: 'rgba(255,255,255,0.4)' }
                  }
                >
                  {rank}
                </span>
                <span
                  className="w-1.5 h-1.5 rounded-full shrink-0"
                  style={{ background: dotColor, boxShadow: `0 0 5px ${dotColor}` }}
                />
                <span
                  className={`flex-1 truncate ${fontName} ${
                    row.isPlayer ? 'text-white font-medium' : 'text-white/85'
                  }`}
                >
                  {row.name}
                </span>
                <span
                  className={`tabular-nums ${fontMass}`}
                  style={row.isPlayer ? { color: dotColor } : { color: 'rgba(255,255,255,0.65)' }}
                >
                  {Math.round(row.mass)}
                </span>
              </div>
            </motion.li>
          );
        })}
      </AnimatePresence>
      {rows.length === 0 && (
        <li className="text-white/35 italic px-1 py-0.5 text-[10px]">— spawning —</li>
      )}
    </motion.ol>
  );
}

/** Overtake hint — micro-callout shown UNDER the leaderboard panel
 *  when the local player isn't already #1. Format:
 *
 *    + 240 to overtake ECHO
 *
 *  Tiny dopamine hook: replaces a static rank with an actionable
 *  goal. The hint pulses softly when the gap shrinks below 100,
 *  signaling "you're CLOSE." Desktop-only — mobile players see
 *  the gap via the death-modal comeback callout instead.
 */
function OvertakeHint({ rows }: { rows: LeaderRow[] }): React.ReactElement | null {
  const myIdx = rows.findIndex((r) => r.isPlayer);
  if (myIdx <= 0) return null;     // not on board, or already #1
  const me = rows[myIdx]!;
  const target = rows[myIdx - 1]!;
  if (!target) return null;
  const gap = Math.max(0, Math.round(target.mass - me.mass));
  if (gap <= 0) return null;
  const close = gap < 100;
  const targetColor = `hsl(${target.hue} 95% 72%)`;
  return (
    <motion.div
      key={`overtake-${target.id}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="mt-2 pt-2 text-center text-[10px] tabular-nums"
      style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
    >
      <span className={close ? 'text-amber-200' : 'text-white/55'}>
        +{' '}
        <span
          className="font-bold"
          style={{
            color: close ? 'rgb(252 211 77)' : 'white',
            textShadow: close ? '0 0 6px rgba(252,211,77,0.6)' : 'none',
          }}
        >
          {gap.toLocaleString()}
        </span>{' '}
        to overtake{' '}
        <span
          className="font-semibold"
          style={{ color: targetColor, textShadow: `0 0 6px ${targetColor}55` }}
        >
          {target.name}
        </span>
      </span>
    </motion.div>
  );
}
